'use client'

import { usePostCreationStore } from '@/lib/stores/postCreationStore';
import { PostType } from '@/lib/types/posts';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Newspaper, BookOpen, MessageSquare, FileText } from 'lucide-react';

const postTypeInfo = {
  [PostType.DEEP_DIVE]: {
    title: 'Deep Dive',
    description: 'In-depth, multi-section analyses and comprehensive reviews.',
    icon: FileText,
    color: 'bg-blue-100 dark:bg-blue-900',
  },
  [PostType.BLOG]: {
    title: 'Blog Style',
    description: 'Narrative-driven content offering detailed insights and opinions.',
    icon: BookOpen,
    color: 'bg-green-100 dark:bg-green-900',
  },
  [PostType.TWITTER]: {
    title: 'Twitter Style',
    description: 'Concise, rapid updates optimized for quick scrolling.',
    icon: MessageSquare,
    color: 'bg-purple-100 dark:bg-purple-900',
  },
  [PostType.NEWS]: {
    title: 'News',
    description: 'Timely, headline-driven updates with clear, fact-based information.',
    icon: Newspaper,
    color: 'bg-orange-100 dark:bg-orange-900',
  },
};

interface PostTypeSelectorProps {
  onSelectType?: (type: PostType) => void;
}

export function PostTypeSelector({ onSelectType }: PostTypeSelectorProps) {
  const { setPostType } = usePostCreationStore();

  const handleSelectType = (type: PostType) => {
    setPostType(type);
    if (onSelectType) {
      onSelectType(type);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Select Post Type</h2>
        <p className="text-muted-foreground">
          Choose the type of content you want to create
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Object.entries(postTypeInfo).map(([type, info]) => {
          const Icon = info.icon;
          return (
            <Card 
              key={type} 
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => handleSelectType(type as PostType)}
            >
              <CardHeader className={`${info.color} rounded-t-lg`}>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-xl">{info.title}</CardTitle>
                  <Icon className="h-8 w-8" />
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                <CardDescription className="text-sm">
                  {info.description}
                </CardDescription>
              </CardContent>
              <CardFooter>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectType(type as PostType);
                  }}
                >
                  Select
                </Button>
              </CardFooter>
            </Card>
          );
        })}
      </div>
    </div>
  );
} 